import React from 'react';
import { useWizardNavigation, useWizardErrors, useLevelUpWizard } from '../../hooks/useLevelUpWizard';
import { WizardStep } from '../../stores/levelUpStore';
import styles from './WizardNavigation.module.css';

interface WizardNavigationProps {
  onCancel: () => void;
  onClose?: () => void;
  onComplete?: (updatedCharacter: any) => void;
}

/**
 * Navigation controls for the wizard (Back, Next/Confirm, Cancel)
 */
export const WizardNavigation: React.FC<WizardNavigationProps> = ({ onCancel, onClose, onComplete }) => {
  const {
    canGoNext,
    canGoBack,
    goNext,
    goBack,
    nextLabel,
    backLabel,
    validation
  } = useWizardNavigation();

  const { currentStep, commitLevelUp } = useLevelUpWizard();

  const { errors, warnings } = useWizardErrors();

  return (
    <div className={styles.wizardNavigation}>
      {/* Error display */}
      {errors.length > 0 && (
        <div className={styles.errorContainer}>
          {errors.map((error, index) => (
            <div key={index} className={styles.error}>
              {error}
            </div>
          ))}
        </div>
      )}

      {/* Warning display */}
      {warnings.length > 0 && (
        <div className={styles.warningContainer}>
          {warnings.map((warning, index) => (
            <div key={index} className={styles.warning}>
              {warning}
            </div>
          ))}
        </div>
      )}

      {/* Validation feedback */}
      {!validation.valid && (
        <div className={styles.validationHint}>
          Complete this step to continue
        </div>
      )}

      {/* Navigation buttons */}
      <div className={styles.buttonContainer}>
        <button
          type="button"
          onClick={goBack}
          disabled={!canGoBack}
          className={styles.backButton}
        >
          {backLabel}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className={styles.cancelButton}
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={() => {
            if (nextLabel === 'Close' && onClose) {
              onClose();
            } else if (nextLabel === 'Confirm & Complete' && onComplete) {
              // Call commitLevelUp which will apply changes and advance to completion step
              commitLevelUp(onComplete);
            } else {
              goNext();
            }
          }}
          disabled={nextLabel === 'Close' ? false : !canGoNext}
          className={styles.nextButton}
        >
          {nextLabel}
        </button>
      </div>
    </div>
  );
};
